/**
 * Section: Tw incident map
 * -----------------------------------------------------------------------------
 * Opens an incident's dialog from its map marker or list button, closes it
 * on a backdrop click, and returns focus to the control that opened it.
 * The dialogs close natively with Escape and their own close button.
 * Wrapped in a guard so a second copy of the section doesn't redefine it.
 *
 */

if (!customElements.get('tw-incident-map')) {

  /**
   * Custom element wrapping the section, its triggers, and dialogs.
   */
  class TwIncidentMap extends HTMLElement {

    /**
     * Bind listeners.
     */
    connectedCallback() {
      this.opener = null
      this.dialogs = [...this.querySelectorAll('dialog')]
      this.handleClick = this.handleClick.bind(this)
      this.handleClose = this.handleClose.bind(this)

      this.picker = this.querySelector('[js-incident="picker"]')
      this.zipForm = this.querySelector('[js-incident="zip-form"]')
      this.zipDialog = this.querySelector('[js-incident="zip-dialog"]')
      this.zipData = null
      this.handleZip = this.handleZip.bind(this)
      this.zipForm?.addEventListener('submit', this.handleZip)
      this.handleChange = this.handleChange.bind(this)

      this.addEventListener('click', this.handleClick)
      this.picker?.addEventListener('change', this.handleChange)
      this.dialogs.forEach((dialog) => {
        dialog.addEventListener('close', this.handleClose)
      })
    }

    /**
     * Remove listeners when the section is removed in the theme editor.
     */
    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick)
      this.picker?.removeEventListener('change', this.handleChange)
      this.zipForm?.removeEventListener('submit', this.handleZip)
      this.dialogs.forEach((dialog) => {
        dialog.removeEventListener('close', this.handleClose)
      })
    }

    /**
     * Open a dialog from a trigger, or close one from its backdrop.
     * @param {MouseEvent} event - Click event from inside the element.
     */
    handleClick(event) {
      if (event.target.tagName === 'DIALOG') {
        event.target.close()

        return
      }

      const storyButton = event.target.closest('[js-incident="zip-story"]')

      if (storyButton) {
        const story = this.querySelector(`dialog[id="${storyButton.dataset.dialog}"]`)

        if (story && !story.open) {
          story.showModal()
        }

        return
      }

      const trigger = event.target.closest('[js-incident="trigger"]')

      if (!trigger) {
        return
      }

      const dialog = this.querySelector(`dialog[id="${trigger.dataset.dialog}"]`)

      if (!dialog || dialog.open) {
        return
      }

      this.opener = trigger
      dialog.showModal()
    }

    /**
     * Look up a ZIP code and open its result.
     * @param {SubmitEvent} event - Submit event from the ZIP form.
     */
    async handleZip(event) {
      event.preventDefault()

      const input = this.zipForm.querySelector('[js-incident="zip-input"]')
      const message = this.zipForm.querySelector('[js-incident="zip-message"]')
      const zip = input.value.trim()

      if (!/^\d{5}$/.test(zip)) {
        message.textContent = 'Enter a 5-digit ZIP code.'
        input.focus()

        return
      }

      try {
        if (!this.zipData) {
          const response = await fetch(this.zipForm.dataset.src)
          this.zipData = await response.json()
        }
      } catch (error) {
        message.textContent = this.zipForm.dataset.notFound

        return
      }

      const result = this.zipData.zips[zip]

      if (!result) {
        message.textContent = `${this.zipForm.dataset.notFound} Try ${Object.keys(this.zipData.zips).slice(0, 3).join(', ')}.`

        return
      }

      message.textContent = ''
      this.fillZip(zip, result)
      this.opener = input
      this.zipDialog.showModal()
    }

    /**
     * Write a ZIP result into the result dialog.
     * @param {string} zip - The ZIP code entered.
     * @param {object} result - That ZIP's record from the data file.
     */
    fillZip(zip, result) {
      const field = (name) => this.zipDialog.querySelector(`[data-zip-field="${name}"]`)
      const storyDialog = result.story
        ? this.querySelector(`dialog[id="TwIncident-${result.story}"]`)
        : null

      field('utility').textContent = result.utility
      field('serves').textContent = `Likely serves ZIP ${zip} · about ${result.population.toLocaleString('en-US')} people`
      field('health').textContent = result.health
      field('other').textContent = result.other
      field('health_detail').textContent = result.health_detail
      field('health_detail').hidden = !result.health_detail
      field('source').textContent = `Source: EPA Safe Drinking Water Information System and service area boundaries, accessed ${this.zipData.updated}. Service areas are estimates, so check with your utility to confirm.`
      field('report').href = result.report
      field('story-wrap').hidden = !storyDialog

      if (storyDialog) {
        const title = storyDialog.querySelector('.tw-incident-map__dialog-copy strong')
        field('story').textContent = title ? title.textContent : storyDialog.getAttribute('aria-label')
        field('story').dataset.dialog = storyDialog.id
      }
    }

    /**
     * Open the incident chosen in the mobile picker.
     */
    handleChange() {
      const dialog = this.querySelector(`dialog[id="${this.picker.value}"]`)

      if (!dialog || dialog.open) {
        return
      }

      this.opener = this.picker
      dialog.showModal()
    }

    /**
     * Return focus to the control that opened the dialog.
     */
    handleClose() {
      if (this.dialogs.some((dialog) => dialog.open)) {
        return
      }

      if (this.opener && this.opener.isConnected) {
        this.opener.focus()
      }

      if (this.picker) {
        this.picker.selectedIndex = 0
      }

      this.opener = null
    }
  }

  customElements.define('tw-incident-map', TwIncidentMap)
}
